import { nextTick } from 'vue'
import type { EprintSession } from '@/composables/format-edit/useEprintSession'

type LatLng = { lat: number; lng: number }

type GoogleMap = {
  setCenter: (c: LatLng) => void
  setZoom: (z: number) => void
}

type GoogleMarker = {
  setMap: (map: GoogleMap | null) => void
  addListener: (ev: string, cb: () => void) => void
}

type GoogleInfoWindow = {
  setContent: (html: string) => void
  open: (map: GoogleMap, marker: GoogleMarker) => void
  close: () => void
}

type PlaceResult = {
  name: string
  vicinity?: string
  geometry: { location: { lat: () => number; lng: () => number } }
  opening_hours?: { open_now?: boolean }
}

type GeocodeResult = {
  geometry: { location: { lat: () => number; lng: () => number } }
}

type PlacesService = {
  nearbySearch: (
    req: unknown,
    cb: (results: PlaceResult[] | null, status: string) => void
  ) => void
}

type GoogleMapsNs = {
  maps: {
    Map: new (el: HTMLElement, opts: unknown) => GoogleMap
    Marker: new (opts: unknown) => GoogleMarker
    InfoWindow: new () => GoogleInfoWindow
    Geocoder: new () => {
      geocode: (
        req: { address: string },
        cb: (results: GeocodeResult[] | null, status: string) => void
      ) => void
    }
    SymbolPath: { CIRCLE: unknown }
    places: {
      PlacesService: new (map: GoogleMap) => PlacesService
      PlacesServiceStatus: { OK: string; ZERO_RESULTS: string }
    }
  }
}

function googleNs(): GoogleMapsNs | undefined {
  return (window as Window & { google?: GoogleMapsNs }).google
}

function escapeHtml(str: string) {
  const div = document.createElement('div')
  div.appendChild(document.createTextNode(str))
  return div.innerHTML
}

export function useEprintStoreMap(session: EprintSession) {
  let googleMapsLoaded = false
  let googleMap: GoogleMap | null = null
  let currentLocationMarker: GoogleMarker | null = null
  let placesService: PlacesService | null = null
  let infoWindow: GoogleInfoWindow | null = null
  let storeMarkers: GoogleMarker[] = []
  let currentPosition: LatLng | null = null
  let mapEl: HTMLElement | null = null

  function bindMapEl(el: HTMLElement | null) {
    mapEl = el
  }

  function initGoogleMaps() {
    if (googleMapsLoaded) return Promise.resolve()
    const g = googleNs()
    if (g && g.maps) {
      googleMapsLoaded = true
      return Promise.resolve()
    }
    return new Promise<void>((resolve, reject) => {
      if (document.querySelector('script[src*="maps.googleapis.com"]')) {
        const checkInterval = setInterval(() => {
          if (googleNs()?.maps) {
            clearInterval(checkInterval)
            googleMapsLoaded = true
            resolve()
          }
        }, 100)
        setTimeout(() => {
          clearInterval(checkInterval)
          if (!googleMapsLoaded) {
            session.mapError = 'マップの読み込みがタイムアウトしました。'
            reject(new Error('Google Maps load timeout'))
          }
        }, 15000)
        return
      }
      const script = document.createElement('script')
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=' +
        process.env.VUE_APP_GOOGLE_MAPS_API_KEY +
        '&libraries=places'
      script.async = true
      script.defer = true
      script.onload = () => {
        googleMapsLoaded = true
        resolve()
      }
      script.onerror = () => {
        session.mapError =
          'マップの読み込みに失敗しました。ページを再読み込みしてください。'
        reject(new Error('Google Maps script load failed'))
      }
      document.head.appendChild(script)
    })
  }

  function searchNearbyKonbini(location: LatLng) {
    const g = googleNs()
    if (!g || !placesService || !googleMap) return
    storeMarkers.forEach((m) => m.setMap(null))
    storeMarkers = []
    placesService.nearbySearch(
      {
        location,
        radius: 1000,
        type: 'convenience_store',
        keyword: 'コンビニ'
      },
      (results, status) => {
        if (status === g.maps.places.PlacesServiceStatus.OK && results) {
          results.forEach((place) => {
            const marker = new g.maps.Marker({
              position: place.geometry.location,
              map: googleMap,
              title: place.name
            })
            marker.addListener('click', () => {
              let openStatus = ''
              if (place.opening_hours) {
                openStatus =
                  '<br><span style="color:' +
                  (place.opening_hours.open_now ? 'green' : 'red') +
                  ';">' +
                  (place.opening_hours.open_now ? '営業中' : '営業時間外') +
                  '</span>'
              }
              const loc = place.geometry.location
              const content =
                '<div style="font-size:13px;max-width:200px;">' +
                '<strong>' +
                escapeHtml(place.name) +
                '</strong><br>' +
                escapeHtml(place.vicinity || '') +
                openStatus +
                '<br><a href="https://www.google.com/maps/dir/?api=1&destination=' +
                loc.lat() +
                ',' +
                loc.lng() +
                '" target="_blank" rel="noopener" style="color:#1a73e8;">ルート案内</a>' +
                '</div>'
              infoWindow?.setContent(content)
              if (googleMap) infoWindow?.open(googleMap, marker)
            })
            storeMarkers.push(marker)
          })
          if (results.length === 0) {
            session.mapError =
              '周辺にコンビニが見つかりませんでした。マップをズームアウトしてみてください。'
          }
        } else if (status === g.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          session.mapError = '周辺にコンビニが見つかりませんでした。'
        } else {
          console.error('Places search failed:', status)
          session.mapError = 'コンビニの検索に失敗しました。'
        }
      }
    )
  }

  function showMapAtLocation(lat: number, lng: number) {
    session.showMap = true
    session.mapError = ''
    nextTick(() => {
      const g = googleNs()
      const mapContainer = mapEl
      if (!g || !mapContainer) return
      const center = { lat, lng }
      if (!googleMap) {
        googleMap = new g.maps.Map(mapContainer, {
          center,
          zoom: 15,
          disableDefaultUI: true
        })
        infoWindow = new g.maps.InfoWindow()
        placesService = new g.maps.places.PlacesService(googleMap)
      } else {
        googleMap.setCenter(center)
        googleMap.setZoom(15)
      }
      if (currentLocationMarker) currentLocationMarker.setMap(null)
      currentLocationMarker = new g.maps.Marker({
        position: center,
        map: googleMap,
        icon: {
          path: g.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#4285F4',
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2
        },
        title: '検索地点'
      })
      searchNearbyKonbini(center)
    })
  }

  function getCurrentPosition() {
    return new Promise<GeolocationPosition>((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('GEOLOCATION_NOT_SUPPORTED'))
        return
      }
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 300000
      })
    })
  }

  function handleGeolocationError(error: { message?: string; code?: number }) {
    if (error.message === 'GEOLOCATION_NOT_SUPPORTED') {
      session.mapError = 'お使いのブラウザは位置情報に対応していません。'
    } else if (error.code === 1) {
      session.mapError =
        '位置情報へのアクセスが拒否されました。ブラウザの設定から位置情報を許可してください。'
    } else if (error.code === 2) {
      session.mapError = '位置情報を取得できませんでした。'
    } else if (error.code === 3) {
      session.mapError =
        '位置情報の取得がタイムアウトしました。もう一度お試しください。'
    } else {
      session.mapError = '位置情報の取得に失敗しました。'
    }
  }

  function searchNearCurrentLocation() {
    session.mapError = ''
    session.isGeolocating = true
    initGoogleMaps()
      .then(() => {
        if (!googleMapsLoaded) {
          session.isGeolocating = false
          return
        }
        return getCurrentPosition()
      })
      .then((position) => {
        if (!position) return
        currentPosition = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        }
        showMapAtLocation(currentPosition.lat, currentPosition.lng)
      })
      .catch((error) => handleGeolocationError(error))
      .then(() => {
        session.isGeolocating = false
      })
  }

  function searchNearResumeAddress() {
    if (!session.resumeAddress) {
      session.mapError = '履歴書に住所が入力されていません。'
      return
    }
    session.mapError = ''
    session.isGeocodingAddress = true
    initGoogleMaps()
      .then(() => {
        const g = googleNs()
        if (!googleMapsLoaded || !g) {
          session.isGeocodingAddress = false
          return
        }
        const geocoder = new g.maps.Geocoder()
        geocoder.geocode(
          { address: session.resumeAddress },
          (results, status) => {
            if (status === 'OK' && results && results.length > 0) {
              const location = results[0].geometry.location
              showMapAtLocation(location.lat(), location.lng())
            } else {
              session.mapError = '住所から位置を特定できませんでした。'
            }
            session.isGeocodingAddress = false
          }
        )
      })
      .catch(() => {
        session.mapError = '住所の検索に失敗しました。もう一度お試しください。'
        session.isGeocodingAddress = false
      })
  }

  function recenterToCurrentLocation() {
    if (currentPosition && googleMap) {
      googleMap.setCenter(currentPosition)
      googleMap.setZoom(15)
      searchNearbyKonbini(currentPosition)
      return
    }
    searchNearCurrentLocation()
  }

  function cleanupMap() {
    storeMarkers.forEach((m) => m.setMap(null))
    storeMarkers = []
    if (currentLocationMarker) {
      currentLocationMarker.setMap(null)
      currentLocationMarker = null
    }
    infoWindow?.close()
    googleMap = null
    session.showMap = false
    session.mapError = ''
  }

  function openMapAppNearCurrentLocation() {
    session.mapError = ''
    session.isGeolocating = true
    if (currentPosition) {
      window.open(
        'https://www.google.com/maps/search/コンビニ/@' +
          currentPosition.lat +
          ',' +
          currentPosition.lng +
          ',15z',
        '_blank'
      )
      session.isGeolocating = false
      return
    }
    getCurrentPosition()
      .then((position) => {
        currentPosition = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        }
        window.open(
          'https://www.google.com/maps/search/コンビニ/@' +
            currentPosition.lat +
            ',' +
            currentPosition.lng +
            ',15z',
          '_blank'
        )
      })
      .catch((error) => handleGeolocationError(error))
      .then(() => {
        session.isGeolocating = false
      })
  }

  function openMapAppNearResumeAddress() {
    if (!session.resumeAddress) {
      session.mapError = '履歴書に住所が入力されていません。'
      return
    }
    const query = encodeURIComponent('コンビニ ' + session.resumeAddress)
    window.open('https://www.google.com/maps/search/' + query, '_blank')
  }

  return {
    bindMapEl,
    searchNearCurrentLocation,
    searchNearResumeAddress,
    recenterToCurrentLocation,
    cleanupMap,
    openMapAppNearCurrentLocation,
    openMapAppNearResumeAddress
  }
}
